import React from 'react';
import { DEFAULT_STATUSES } from '@/constants/leadStatuses';
import AppBox from '@/components/AppBox';
import AppDropdown from '@/components/AppDropdown';
import { TableFilters, SortOrder } from '@/types/filters';
import AppButton from '@/components/AppButton';
import AppText from '@/components/AppText';

interface FiltersProps {
  filters: TableFilters;
  onChange: (filters: TableFilters) => void;
}

const Filters: React.FC<FiltersProps> = ({ filters, onChange }) => {
  const handleFilterChange = (key: keyof TableFilters, value: any) => {
    onChange({ ...filters, [key]: value });
  };

  return (
    <AppBox className="z-[40] w-full justify-end">
      <AppBox 
        className="
          items-center
          w-full
          2xl:w-1/2
          flex-col sm:flex-row
          space-y-2 sm:space-y-0
          sm:justify-between md:justify-start md:gap-4
          sm:gap-2
        "
      >
        <AppText variant="h6" className="self-start sm:self-center md:mr-4">
          Filters:
        </AppText>

        <AppBox 
          className="
            w-full sm:flex-1 
            grid gap-2
            grid-cols-1 
            sm:grid-cols-2 
            lg:grid-cols-4 
            xl:grid-cols-5
          "
        >
          {/* Phone Filter */}
          <AppDropdown
            className="w-full"
            label={filters.phone ? {
              'with_phone': 'With Phone',
              'without_phone': 'Without Phone'
            }[filters.phone] || 'Phone' : 'Phone'}
            value={filters.phone || ''}
            onChange={(value) => handleFilterChange('phone', value)}
            items={[
              { value: '', label: 'All Leads' },
              { value: 'with_phone', label: 'With Phone Number' },
              { value: 'without_phone', label: 'Without Phone Number' }
            ]}
          />

          {/* Type Filter */}
          <AppDropdown
            className="w-full"
            label={filters.type.value || 'Type'}
            value={filters.type.value || ''}
            onChange={(value) => handleFilterChange('type', {
              ...filters.type,
              value: value as string
            })}
            items={[
              { value: '', label: 'All Types' },
              { value: 'Message', label: 'Message' },
              { value: 'Missed Call', label: 'Missed Call' },
              { value: 'Answered Call', label: 'Answered Call' }
            ]}
          />

          {/* Date Sort */}
          <AppDropdown
            className="w-full"
            label={filters.date ? {
              'asc': 'Oldest First',
              'desc': 'Newest First'
            }[filters.date] || 'Date' : 'Date'}
            value={filters.date || ''}
            onChange={(value) => handleFilterChange('date', value)}
            items={[
              { value: '', label: 'Default' },
              { value: 'asc', label: 'Oldest First' },
              { value: 'desc', label: 'Newest First' }
            ]}
          />

          {/* Status Filter */}
          <AppDropdown
            className="w-full capitalize"
            label={filters.status ? {
              'no_status': 'No Status',
              'custom': 'Custom Status',
              ...DEFAULT_STATUSES.reduce((acc, status) => ({
                ...acc,
                [status]: status
              }), {})
            }[filters.status] || 'Status' : 'Status'}
            value={filters.status || ''}
            onChange={(value) => handleFilterChange('status', value)}
            items={[
              { value: '', label: 'All Statuses' },
              { value: 'no_status', label: 'No Status' },
              { value: 'custom', label: 'Custom Status' },
              ...DEFAULT_STATUSES.map(status => ({
                value: status,
                label: status
              }))
            ]}
          />

          {/* Reset Button */}
          <AppButton
            className="w-full"
            variant="outlined"
            onClick={() => {
              onChange({
                phone: null,
                type: { sort: null, value: null },
                date: null,
                status: null
              });
            }}
          >
            Reset Filters
          </AppButton>
        </AppBox>
      </AppBox>
    </AppBox>
  );
};

export default Filters; 